import { Button, Switch } from '@you/ui'
import { useNavigate } from 'react-router-dom'
import { formatDateTime } from '@/lib/utils'
import { Dataset } from '@/api/types/dataset.type'
import { useKnowledgeStore } from '@/stores/knowledgeStore'

interface DatasetListProps {
  data?: Dataset[]
  onEdit?: (dataset: Dataset) => void
  onDelete?: (id: string) => void
  onStatusChange?: (id: string, status: boolean) => void
  isLoading?: boolean
  isToggling?: boolean
}

const statusMap = {
  enabled: { label: '已启用', color: 'bg-green-100 text-green-700' },
  disabled: { label: '已停用', color: 'bg-gray-100 text-gray-500' },
}

const DatasetList = ({
  data,
  onEdit,
  onDelete,
  onStatusChange,
  isLoading = false,
  isToggling = false,
}: DatasetListProps) => {
  // 使用传入的数据
  const datasets = data || []
  const navigate = useNavigate()
  const { setCurrentDataset } = useKnowledgeStore()

  /**
   * 处理状态切换
   */
  const handleStatusToggle = (id: string, checked: boolean) => {
    onStatusChange?.(id, checked)
  }

  /**
   * 处理编辑操作
   */
  const handleEdit = (dataset: Dataset) => {
    setCurrentDataset(dataset)
    onEdit?.(dataset)
  }

  /**
   * 处理删除操作
   */
  const handleDelete = (id: string) => {
    onDelete?.(id)
  }

  /**
   * 处理数据集标题点击
   */
  const handleDatasetClick = (dataset: Dataset) => {
    setCurrentDataset(dataset)
    navigate(`/knowledge/dataset-detail?id=${dataset.id}`)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">加载中...</div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white border border-[#F7F9FF] rounded-lg overflow-hidden">
      {/* 表头 - 固定 */}
      <div
        className="flex-shrink-0 border-b"
        style={{ borderColor: '#F7F9FF' }}
      >
        <table className="w-full table-fixed">
          <thead className="[&>tr>th]:text-center [&>tr>th]:text-xs [&>tr>th]:font-medium [&>tr>th]:uppercase [&>tr>th]:tracking-wider [&>tr>th]:px-6 [&>tr>th]:py-3">
            <tr style={{ backgroundColor: '#EFF3FD', height: '40px' }}>
              <th className="w-1/3" style={{ color: '#333333' }}>
                数据集
              </th>
              <th className="w-1/6" style={{ color: '#333333' }}>
                文件数量
              </th>
              <th className="w-1/6" style={{ color: '#333333' }}>
                状态
              </th>
              <th className="w-1/4" style={{ color: '#333333' }}>
                更新时间
              </th>
              <th className="w-1/6" style={{ color: '#333333' }}>
                操作
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {/* 表格内容 - 可滚动 */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full table-fixed">
          <tbody className="divide-y" style={{ borderColor: '#F7F9FF' }}>
            {datasets.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-[#F9FAFF] [&>td]:text-center [&>td]:text-base [&>td]:px-6 [&>td]:py-4"
                style={{ height: '80px', borderColor: '#F7F9FF' }}
              >
                <td className="w-1/3" style={{ color: '#8047FF' }}>
                  <button
                    onClick={() => handleDatasetClick(item)}
                    className="hover:underline cursor-pointer bg-transparent border-none p-0"
                  >
                    {item.name}
                  </button>
                </td>
                <td className="text-gray-700 w-1/6">{item.filesNum}</td>
                <td className="w-1/6">
                  <div className="flex items-center justify-center space-x-1">
                    <Switch
                      checked={item.status === 1}
                      onCheckedChange={(checked) =>
                        handleStatusToggle(item.id, checked)
                      }
                      disabled={isToggling}
                    />
                    <span className={`inline-block px-2 rounded-full text-xs`}>
                      {item.status === 1
                        ? statusMap.enabled.label
                        : statusMap.disabled.label}
                    </span>
                  </div>
                </td>
                <td className="text-gray-500 w-1/4">
                  {formatDateTime(item.updateTime)}
                </td>
                <td className="space-x-2 w-1/6">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(item)}
                  >
                    导入
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    删除
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DatasetList
